/**
 * The lore deck: what STILLSUN says to you while it is loading.
 *
 * Owner brief (2026-09-04): "instead of a loading bar on that first page, make it part of a random
 * part of the lore that writes out until it finishes and then it loads to the enter screen. do the
 * same for when you deploy, another part of the story."
 *
 * So a wait is not a progress bar any more. It is the only unhurried reading in the game, and it
 * has one job: make somebody who has never heard of the Stillsun want to know what happened here. Every
 * passage is therefore a *scene* or a *confession*, never a fact sheet — nothing in this file
 * explains a mechanic, names a currency, or tells you what a button does. The tips deck
 * (`tips.ts`) still exists and still does that job; this is the other thing.
 *
 * Rules for anything added here:
 * - 50–90 words. Shorter reads as a caption; longer outruns the load and gets held mid-thought.
 * - Somebody says it. Either one of the six quest voices (`packages/shared/src/quest-library.ts`
 *   — Isolde Vane, Sabel Roque, Ferryman Wren, Deacon Ott, Pell, The Choir), whose tone is
 *   authored there and must match, or the Ledger, which is the Compact's own record and is the
 *   only voice allowed to be flat.
 * - One turn. Each passage sets something up and then takes it away in its last sentence. A
 *   passage that only describes is filler; cut it or give it a turn.
 * - No second person plural, no "you must", no instructions. Nobody here is addressing a player.
 */

export interface LorePassage {
  /** Stable id, used to avoid showing the same passage twice in a row. */
  id: string;
  /** Two or three words. Sits above the passage in Bebas. */
  title: string;
  /** Who is speaking. "the Ledger" is the Compact's record, not a person. */
  speaker: string;
  /** 50–90 words. Typed out one character at a time; keep the punctuation clean. */
  text: string;
}

export const LORE: readonly LorePassage[] = [
  {
    id: 'stillsun',
    title: 'The Stillsun',
    speaker: 'the Ledger',
    text: 'The sun came down to the horizon in the world’s first age and sat there, and it has not risen or set since. Sunward of Lanternfall the fields still smoke from the day it stopped burning. Nightward the frost has never once broken. Everyone who has ever lived under it keeps to the long dusk between those two edges, calls it the Stillsun, and does not look at it.',
  },
  {
    id: 'candle',
    title: 'The Fall',
    speaker: 'the Ledger',
    text: 'The cathedral came down when the sun stopped, in three long cracks that took three hundred years to stop widening. The bell tower fell first, out into the barrows. The choir loft went next, into the hollow they call the Stones. The nave held, bent but standing, and the living built Lanternfall’s wall along its spine. Three hundred years of walking its bones have not settled whether the nave held out of mercy or out of spite.',
  },
  {
    id: 'first-lantern',
    title: 'The First Lantern',
    speaker: 'Isolde Vane',
    text: 'The woman who lit the first lantern here did it to be found. She had walked out of the ruin with nine people and lost four of them in the dark, and she thought a light would bring the rest in. It did. It also brought everything else. We have kept the lanterns burning for three hundred years and we have never once agreed on what they are for.',
  },
  {
    id: 'chorus',
    title: 'Before It Sang',
    speaker: 'Deacon Ott',
    text: 'The Choir was a congregation once, or so the old service says. When the sun stopped they were mid-hymn, and someone decided that stopping the hymn would stop the sun stopping — bad theology that outlived the men who taught it. They sang through the hunger and sang through the dying, and what came out the far side was not a hundred voices and not one. It was something in between, and it is singing still. We call it the Choir. It calls itself we.',
  },
  {
    id: 'remembers',
    title: 'What It Remembers',
    speaker: 'the Choir',
    text: 'We remember hands. We remember the weight of a cup and the give of a mattress and the particular way a floor is cold at the fourth bell. We remember a name being called across a corridor and turning, and we have kept turning for three hundred years, and nobody is there. We do not remember dying. We have looked. There is a gap where it should be and we have put your name in it.',
  },
  {
    id: 'ferry',
    title: 'One Way',
    speaker: 'Ferryman Wren',
    text: 'People ask why the Ferry only runs nightward, and I tell them it is a matter of fuel, which is true and is not the reason. The reason is that nothing has ever come back the other way to sell a return leg to. I have taken payment for two hundred and eleven passages out. I have run every one of them. I have never once had to run a second.',
  },
  {
    id: 'shrinkage',
    title: 'Shrinkage',
    speaker: 'Quartermaster Sabel Roque',
    text: 'Shrinkage is the word the Compact uses for a loss it cannot itemise. It is an accounting term and it is a mercy, because it costs nothing to write and it closes the column. I have entered four hundred and six kilos of shrinkage this season. I know the weight of a grown Waker. I have known it for a long time, and I have gone on writing the word, because the column has to close.',
  },
  {
    id: 'pell',
    title: 'Tuesday',
    speaker: 'Pell',
    text: 'My dad went in on a Tuesday. He took the green toolroll with his initials burned in the flap and he said he would be back before the dousing. That was two hundred and forty dousings ago. I have counted. Isolde says counting is not the same as waiting but she counts the lanterns every single night, so I do not think she believes that either.',
  },
  {
    id: 'dousing',
    title: 'The Dark Hour',
    speaker: 'Isolde Vane',
    text: 'Once a day we put every lantern in Lanternfall out for an hour. We learned early that the Hymns find us by the light, so for that hour we sit in the dark with our hands on the table and we do not speak. And every night, out past the line, they come and stand where the light would have been. They do not cross. In three hundred years not one of them has crossed.',
  },
  {
    id: 'berth',
    title: 'The Berth',
    speaker: 'the Choir',
    text: 'There is a berth kept made up in the Barrows, sheets turned down, and it has been ready for three hundred years. We do not know whose it is any more. We change the linen. We keep it aired. When one of you comes up the corridor we straighten the corner of it, because it might be you, and we would like the room to be right when whoever it is finally comes home.',
  },
  {
    id: 'wakers',
    title: 'Wakers',
    speaker: 'the Ledger',
    text: 'A Waker is somebody who goes out into the Downs and comes back with something still worth having. The word started as a joke about waking the dead. It stopped being a joke the year the dead started answering back. The Compact registered four hundred Wakers this season. It expects to renew two hundred and eighty of those registrations next season and has budgeted accordingly.',
  },
  {
    id: 'liturgy',
    title: 'Quarter Speed',
    speaker: 'Deacon Ott',
    text: "I have set the Cantors' song to liturgy for nine years and taught it to children, and last winter I put a recording at quarter speed to check a phrase. There are words in it. They are in the old tongue. It is a man saying a woman's name and then the word please, forty-one times, and then starting again. I have not stopped the evening service. I do not know how to say why.",
  },
  {
    id: 'band',
    title: 'The Downs',
    speaker: 'the Ledger',
    text: 'Walk sunward from Lanternfall and the fields still smoke, embers that never finished falling. Walk nightward and the frost thickens until it takes your breath before it takes your steps. Everyone who has ever lived here has lived inside the long dusk between those two ways of dying, on ground the surveyors call the Vigil Downs. It is not a country. It is the distance between two kinds of ending, and we have made a home of it.',
  },
  {
    id: 'rate',
    title: 'The Rate',
    speaker: 'Quartermaster Sabel Roque',
    text: 'The Compact pays by weight for bell-bronze, by the coil for wire, and by the piece for anything that still answers. It pays nothing for a congregant’s tag, which is why they come back by the sackful and go into a drawer in my office. There are eleven hundred in there. I have never been able to work out the correct entry for a drawer like that, so it is not in the ledger at all.',
  },
  {
    id: 'thirty-one',
    title: 'Thirty-One',
    speaker: 'Isolde Vane',
    text: 'There are thirty-one lanterns in my window and each one is a person who has not come back yet. I light them at dusk and put them out at dawn and I do not take one down until I am certain, and I am almost never certain. People think it is a memorial. It is not. A memorial is for the dead. I have not agreed to that about a single one of them.',
  },
  {
    id: 'linings',
    title: 'The Linings',
    speaker: 'Ferryman Wren',
    text: 'The bundles that go out on the Ferry are wrapped to a spec and I do not open them, which is the arrangement and has always been the arrangement. Somebody asked me once what is chalked on the underside of the wrappings. I told them it was a lot number. It is not a lot number. It is four digits and a letter, and in the old parish rolls that was how you wrote down where a family kept its pew.',
  },
  {
    id: 'emberhound',
    title: 'What Adapted',
    speaker: 'the Ledger',
    text: 'Nothing that hunts the Downs after dusk was always like this. The old bestiaries call them dogs, or wolves, or nothing worth a name, from before the sun stopped and the hunger changed what a body needs to be. The emberhounds run in what light survives of morning, they track by warmth, and they will not go anywhere near a lit lantern. Nobody has ever established why.',
  },
  {
    id: 'aubrey',
    title: 'The Open Channel',
    speaker: 'Isolde Vane',
    text: "I broadcast on the open channel every dusk in case anyone out there still has a receiver. For three hundred nights it was weather and names and the hour of the dousing, into nothing. Last winter something began walking toward the sound. It stops at the lantern line and it waits until I stop speaking. On the ninth night it said a word back. It was my name, and it was said in my son's voice.",
  },
  {
    id: 'hymnal',
    title: 'The Congregation',
    speaker: 'Deacon Ott',
    text: 'People are unkind about the Hymnal and I understand why. We are the ones who kneel to the thing that killed the congregation. But consider what we are actually claiming: that the Downs are not haunted, they are inhabited; that what walks the barrows is not a corpse moving on its own but a person still wearing it. Every argument against us rests on hoping I am wrong. Nobody has ever offered me evidence.',
  },
  {
    id: 'yard',
    title: 'The Yard',
    speaker: 'Isolde Vane',
    text: 'We built the yard so that the first thing a new Waker does in their life is survivable. A wall, some crates, three of them brought in from the dunes and let loose. It is not the Downs and nobody pretends it is. But everyone who has ever come home to Lanternfall walked out of that yard first, and everyone who did not come home walked out of it too, and I remember which of them liked it.',
  },
];

/**
 * A passage at random, never the one just shown.
 *
 * `exclude` is the previous passage's id: the boot shows one and the first deploy of the session
 * shows another, and getting the same three hundred words twice in five minutes would read as a
 * bug rather than as a deck.
 */
export function randomLore(exclude?: string): LorePassage {
  const pool = LORE.filter((p) => p.id !== exclude);
  const list = pool.length > 0 ? pool : LORE;
  return list[Math.floor(Math.random() * list.length)] ?? LORE[0]!;
}

/**
 * The passage split into subtitle lines: at most `perLine` characters, broken on words.
 *
 * The deploy cinematic shows two of these at a time, cinema style, so the break has to fall
 * where a reader would breathe rather than at a fixed column — a line that ends mid-clause is
 * the difference between a subtitle and a wrapped paragraph.
 */
export function subtitleLines(text: string, perLine = 62): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const next = line.length === 0 ? word : `${line} ${word}`;
    if (next.length > perLine && line.length > 0) {
      lines.push(line);
      line = word;
      continue;
    }
    line = next;
    // A sentence that ends comfortably short of the limit gets its own line: the pause is the
    // point, and carrying the next sentence up onto it throws the rhythm away.
    if (/[.?!]$/.test(word) && line.length > perLine * 0.55) {
      lines.push(line);
      line = '';
    }
  }
  if (line.length > 0) lines.push(line);
  return lines;
}
